import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useToast } from '../components/ui/toast';
import { requestPasswordReset, AuthError } from '../lib/auth';
import {
  Button,
  Input,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Label,
  Logo,
} from '../components/ui';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { addToast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      addToast({
        variant: 'destructive',
        title: t('auth.email') + ' ' + t('common.required').toLowerCase(),
      });
      return;
    }

    setIsLoading(true);

    try {
      await requestPasswordReset(email);
      setIsSubmitted(true);
      addToast({
        variant: 'success',
        title: t('auth.resetEmailSent'),
      });
    } catch (err) {
      if (err instanceof AuthError) {
        addToast({ variant: 'destructive', title: err.message });
      } else {
        addToast({ variant: 'destructive', title: t('errors.serverError') });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center space-y-2 text-center">
          <Logo size="xl" className="mb-2" />
          <p className="text-sm text-muted-foreground">Sistema de Gestion</p>
        </div>

        <Card className="shadow-lg">
          {isSubmitted ? (
            <CardContent className="pt-6 space-y-6">
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                  <CheckCircle className="h-6 w-6 text-success" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">
                    {t('auth.resetEmailSent')}
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('auth.resetEmailInfo')}
                  </p>
                </div>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t('common.back')} {t('auth.login').toLowerCase()}
                </Link>
              </div>
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-1">
                <CardTitle>{t('auth.forgotPassword')}</CardTitle>
                <CardDescription>
                  Ingrese su email y le enviaremos instrucciones para restablecer su contrasena.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="tu@email.com"
                        autoComplete="email"
                        required
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    loading={isLoading}
                  >
                    {isLoading ? 'Enviando...' : 'Enviar instrucciones'}
                  </Button>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      {t('common.back')} {t('auth.login').toLowerCase()}
                    </Link>
                  </div>
                </form>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
